import React, { useState } from 'react';

interface Chapter {
  year: string;
  chapter: string;
  title: string;
  description: string;
  keyMoment: string;
  quote: string;
  icon: React.ReactNode;
  stats: { label: string; value: string }[];
  cumulativeClients: number;
  cumulativeProjects: number;
}

const IconSpark = () => (
  <svg viewBox="0 0 40 40" fill="none" width="100%" height="100%">
    <path d="M20 4l3 10 10 3-10 3-3 10-3-10-10-3 10-3 3-10z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    <circle cx="20" cy="20" r="2" fill="currentColor" />
  </svg>
);
const IconHand = () => (
  <svg viewBox="0 0 40 40" fill="none" width="100%" height="100%">
    <path d="M6 20l6-4 8 4 8-4 6 4-6 10h-16z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    <path d="M14 20v-6M26 20v-6M20 20v-8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);
const IconLayers = () => (
  <svg viewBox="0 0 40 40" fill="none" width="100%" height="100%">
    <path d="M20 6l-13 6 13 6 13-6-13-6z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    <path d="M7 18l13 6 13-6M7 24l13 6 13-6" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
  </svg>
);
const IconBadge = () => (
  <svg viewBox="0 0 40 40" fill="none" width="100%" height="100%">
    <circle cx="20" cy="16" r="10" stroke="currentColor" strokeWidth="1.2" />
    <path d="M14 24l-3 10 9-5 9 5-3-10" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    <path d="M16 16l3 3 5-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IconGlobe = () => (
  <svg viewBox="0 0 40 40" fill="none" width="100%" height="100%">
    <circle cx="20" cy="20" r="14" stroke="currentColor" strokeWidth="1.2" />
    <path d="M6 20h28M20 6c5 5 5 23 0 28M20 6c-5 5-5 23 0 28" stroke="currentColor" strokeWidth="1.2" />
  </svg>
);
const IconForward = () => (
  <svg viewBox="0 0 40 40" fill="none" width="100%" height="100%">
    <circle cx="20" cy="20" r="14" stroke="currentColor" strokeWidth="1.2" strokeDasharray="3 3" />
    <path d="M14 20h10m-3-4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const chapters: Chapter[] = [
  {
    year: '2019',
    chapter: 'Chapitre 01',
    title: 'Fondation de MIID',
    description:
      'Naissance de la vision: révolutionner le paysage technologique mauritanien avec des solutions innovantes et sur mesure.',
    keyMoment: 'Création officielle · Équipe fondatrice réunie',
    quote: 'Nous avons commencé avec une idée simple: faire de la tech qui compte vraiment.',
    icon: <IconSpark />,
    stats: [
      { label: 'Fondateurs', value: '3' },
      { label: 'Clients', value: '2' },
    ],
    cumulativeClients: 2,
    cumulativeProjects: 3,
  },
  {
    year: '2020',
    chapter: 'Chapitre 02',
    title: 'Premiers Clients',
    description:
      'Conquête des premiers clients avec des solutions web sur mesure qui ont transformé leur présence digitale.',
    keyMoment: 'Premier contrat grand compte signé',
    quote: 'La confiance des premiers clients a validé tout ce en quoi nous croyions.',
    icon: <IconHand />,
    stats: [
      { label: 'Clients', value: '10+' },
      { label: 'Projets', value: '15+' },
    ],
    cumulativeClients: 10,
    cumulativeProjects: 15,
  },
  {
    year: '2021',
    chapter: 'Chapitre 03',
    title: 'Expansion des Services',
    description:
      'Élargissement de notre gamme: développement mobile, cloud computing et cybersécurité pour couvrir tous les besoins.',
    keyMoment: 'Portfolio complet · Équipe doublée',
    quote: 'Chaque nouvelle expertise nous rapprochait d\'un partenariat total avec nos clients.',
    icon: <IconLayers />,
    stats: [
      { label: 'Services', value: '6' },
      { label: 'Technologies', value: '20+' },
    ],
    cumulativeClients: 28,
    cumulativeProjects: 42,
  },
  {
    year: '2023',
    chapter: 'Chapitre 04',
    title: 'Reconnaissance Régionale',
    description:
      'Certifications internationales et reconnaissance comme leader tech en Afrique de l\'Ouest.',
    keyMoment: '8 certifications · 3 prix régionaux',
    quote: 'La reconnaissance internationale a été la récompense d\'une exigence quotidienne.',
    icon: <IconBadge />,
    stats: [
      { label: 'Certifications', value: '8' },
      { label: 'Prix', value: '3' },
    ],
    cumulativeClients: 55,
    cumulativeProjects: 80,
  },
  {
    year: '2024',
    chapter: 'Chapitre 05',
    title: 'Vision Globale',
    description:
      'Expansion internationale et partenariats stratégiques pour porter l\'innovation mauritanienne au monde.',
    keyMoment: 'Entrée marchés européens · Premier bureau international',
    quote: 'Nos racines sont mauritaniennes, notre ambition est mondiale.',
    icon: <IconGlobe />,
    stats: [
      { label: 'Pays', value: '5' },
      { label: 'Partenaires', value: '12' },
    ],
    cumulativeClients: 90,
    cumulativeProjects: 140,
  },
  {
    year: '2026+',
    chapter: 'Chapitre 06',
    title: 'La suite s\'écrit',
    description:
      'IA générative, plateformes SaaS et automatisation à grande échelle. La prochaine page de notre histoire.',
    keyMoment: 'Vous pourriez en faire partie',
    quote: 'Le prochain chapitre se construit aujourd\'hui — avec ceux qui croient qu\'on peut faire mieux.',
    icon: <IconForward />,
    stats: [
      { label: 'Ambition', value: '∞' },
      { label: 'Vision', value: 'MIID 2030' },
    ],
    cumulativeClients: 150,
    cumulativeProjects: 220,
  },
];

const InteractiveTimeline: React.FC = () => {
  const [active, setActive] = useState(4);
  const ch = chapters[active];
  const isFuture = active === chapters.length - 1;

  // Growth chart data
  const maxClients = Math.max(...chapters.map((c) => c.cumulativeClients));
  const chartPoints = chapters.slice(0, active + 1).map((c, i) => ({
    x: (i / (chapters.length - 1)) * 260,
    y: 80 - (c.cumulativeClients / maxClients) * 70,
  }));
  const chartPath = chartPoints
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`)
    .join(' ');

  const progress = (active / (chapters.length - 1)) * 100;

  return (
    <section className="epopee-section">
      <div className="epopee-inner">
        {/* Header */}
        <div className="reveal epopee-header">
          <div className="sec-tag">Histoire</div>
          <h2 className="sec-title">
            Notre <em>Épopée</em>
          </h2>
          <div className="gold-bar" />
          <p className="epopee-intro">
            Six chapitres, six jalons. L'histoire de MIID racontée par ceux qui la vivent.
          </p>
        </div>

        {/* Horizontal timeline bar */}
        <div className="reveal d1 epopee-timeline">
          <div className="epopee-line-bg" />
          <div className="epopee-line-fill" style={{ width: `${progress}%` }} />
          {chapters.map((c, i) => {
            const isActive = i === active;
            const isPassed = i < active;
            const isLast = i === chapters.length - 1;
            return (
              <button
                key={c.year}
                onClick={() => setActive(i)}
                className={`epopee-dot-wrap ${isActive ? 'is-active' : ''}`}
                aria-label={`Chapitre ${i + 1}: ${c.title}`}
              >
                <div
                  className={`epopee-dot ${isActive ? 'is-active' : ''} ${isPassed ? 'is-passed' : ''} ${isLast ? 'is-future' : ''}`}
                />
                <div className={`epopee-year ${isActive ? 'is-active' : ''}`}>{c.year}</div>
              </button>
            );
          })}
        </div>

        {/* Chapter card */}
        <div className="reveal d2 epopee-chapter">
          {/* Left: drop cap + icon + stats */}
          <div className="epopee-left">
            <div className="epopee-chapter-label">{ch.chapter}</div>
            <div className={`epopee-dropcap ${isFuture ? 'is-future' : ''}`}>{ch.year}</div>

            <div className="epopee-icon-wrap">
              <div className="epopee-icon">{ch.icon}</div>
            </div>

            <div className="epopee-stats">
              {ch.stats.map((s) => (
                <div key={s.label} className="epopee-stat">
                  <div className="epopee-stat-value">{s.value}</div>
                  <div className="epopee-stat-label">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: narrative + quote + chart */}
          <div className="epopee-right">
            <h3 className="epopee-title">{ch.title}</h3>

            <p className="epopee-desc">{ch.description}</p>

            {/* Key moment */}
            <div className="epopee-moment">
              <div className="epopee-moment-bar" />
              <div>
                <div className="epopee-moment-label">Moment clé</div>
                <div className="epopee-moment-text">{ch.keyMoment}</div>
              </div>
            </div>

            {/* Quote */}
            <blockquote className="epopee-quote">
              <span className="epopee-quote-mark">"</span>
              <span>{ch.quote}</span>
            </blockquote>

            {/* Growth chart */}
            <div className="epopee-chart">
              <div className="epopee-chart-head">
                <span>Croissance clients cumulés</span>
                <span className="epopee-chart-now">● {ch.cumulativeClients}</span>
              </div>
              <svg viewBox="0 0 260 80" preserveAspectRatio="none" style={{ width: '100%', height: '80px' }}>
                <defs>
                  <linearGradient id="epopeeGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--blue)" stopOpacity=".3" />
                    <stop offset="100%" stopColor="var(--blue)" stopOpacity="0" />
                  </linearGradient>
                  <linearGradient id="epopeeLine" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="var(--blue)" />
                    <stop offset="100%" stopColor="var(--gold)" />
                  </linearGradient>
                </defs>

                {/* Grid lines */}
                {[20, 40, 60].map((y) => (
                  <line key={y} x1="0" y1={y} x2="260" y2={y} stroke="var(--border)" strokeWidth=".5" />
                ))}

                {/* Area */}
                {chartPoints.length > 1 && (
                  <path
                    d={`${chartPath} L ${chartPoints[chartPoints.length - 1].x} 80 L 0 80 Z`}
                    fill="url(#epopeeGrad)"
                  />
                )}
                {/* Line */}
                <path d={chartPath} stroke="url(#epopeeLine)" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />

                {/* Dots */}
                {chartPoints.map((p, i) => (
                  <circle
                    key={i}
                    cx={p.x}
                    cy={p.y}
                    r={i === chartPoints.length - 1 ? 4 : 2.5}
                    fill={i === chartPoints.length - 1 ? 'var(--gold)' : 'var(--blue)'}
                    stroke="var(--white)"
                    strokeWidth="1.5"
                  >
                    {i === chartPoints.length - 1 && (
                      <animate attributeName="r" values="4;6;4" dur="1.8s" repeatCount="indefinite" />
                    )}
                  </circle>
                ))}
              </svg>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="reveal d3 epopee-nav">
          <button
            className="epopee-nav-btn"
            onClick={() => setActive((a) => Math.max(0, a - 1))}
            disabled={active === 0}
            aria-label="Chapitre précédent"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M10 4L6 8l4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Précédent
          </button>
          <div className="epopee-nav-counter">
            Chapitre {active + 1} / {chapters.length}
          </div>
          <button
            className="epopee-nav-btn"
            onClick={() => setActive((a) => Math.min(chapters.length - 1, a + 1))}
            disabled={active === chapters.length - 1}
            aria-label="Chapitre suivant"
          >
            Suivant
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>

      <style>{`
        .epopee-section {
          padding: 6rem 0;
          background: var(--white);
          position: relative;
          overflow: hidden;
        }
        .epopee-inner {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
          position: relative;
        }
        .epopee-header { margin-bottom: 3rem; max-width: 620px; }
        .epopee-intro {
          font-family: 'Montserrat', sans-serif;
          font-size: .88rem;
          font-weight: 300;
          color: var(--ink2);
          line-height: 1.8;
        }

        /* Horizontal timeline */
        .epopee-timeline {
          position: relative;
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.5rem 0 2.5rem;
          margin-bottom: 3rem;
        }
        .epopee-line-bg {
          position: absolute;
          top: calc(1.5rem + 9px);
          left: 10px;
          right: 10px;
          height: 2px;
          background: var(--border);
          z-index: 0;
        }
        .epopee-line-fill {
          position: absolute;
          top: calc(1.5rem + 9px);
          left: 10px;
          height: 2px;
          background: linear-gradient(90deg, var(--blue), var(--gold));
          z-index: 1;
          transition: width .6s cubic-bezier(.4, 0, .2, 1);
        }
        .epopee-dot-wrap {
          position: relative;
          z-index: 2;
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: .55rem;
        }
        .epopee-dot {
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: var(--white);
          border: 2px solid var(--border);
          transition: all .3s ease;
        }
        .epopee-dot.is-passed {
          background: var(--blue);
          border-color: var(--blue);
        }
        .epopee-dot.is-active {
          background: var(--gold);
          border-color: var(--gold);
          box-shadow: 0 0 0 5px rgba(184,146,42,.2);
          transform: scale(1.15);
        }
        .epopee-dot.is-future {
          border-style: dashed;
          border-color: var(--blue);
        }
        .epopee-dot.is-future.is-active {
          background: var(--gold);
          border-style: solid;
        }
        .epopee-year {
          font-family: 'Cormorant Garamond', Georgia, serif;
          font-size: .85rem;
          font-weight: 600;
          color: var(--ink2);
          transition: color .3s ease;
          white-space: nowrap;
        }
        .epopee-year.is-active { color: var(--blue); font-size: 1rem; }

        /* Chapter card */
        .epopee-chapter {
          display: grid;
          grid-template-columns: 280px 1fr;
          gap: 0;
          background: var(--cream);
          border: 1px solid var(--border);
          overflow: hidden;
          margin-bottom: 2rem;
        }
        .epopee-left {
          padding: 2rem;
          background: linear-gradient(180deg, var(--blue-pale) 0%, var(--cream) 100%);
          border-right: 1px solid var(--border);
          display: flex;
          flex-direction: column;
        }
        .epopee-chapter-label {
          font-family: 'Montserrat', sans-serif;
          font-size: .6rem;
          font-weight: 600;
          letter-spacing: .22em;
          text-transform: uppercase;
          color: var(--gold-dark);
          margin-bottom: .5rem;
        }
        .epopee-dropcap {
          font-family: 'Cormorant Garamond', Georgia, serif;
          font-size: 5.5rem;
          font-weight: 600;
          line-height: .9;
          background: linear-gradient(135deg, var(--blue) 0%, var(--gold) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          font-style: italic;
          margin-bottom: 1.5rem;
          letter-spacing: -.02em;
        }
        .epopee-dropcap.is-future { font-size: 3.8rem; }
        .epopee-icon-wrap {
          margin: auto 0;
          padding: 1.8rem 0;
          display: flex;
          justify-content: center;
        }
        .epopee-icon {
          width: 80px;
          height: 80px;
          color: var(--blue);
          opacity: .85;
        }
        .epopee-stats {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1px;
          background: var(--border);
          border: 1px solid var(--border);
          margin-top: auto;
        }
        .epopee-stat {
          background: var(--white);
          padding: .9rem .8rem;
          text-align: center;
        }
        .epopee-stat-value {
          font-family: 'Cormorant Garamond', Georgia, serif;
          font-size: 1.6rem;
          font-weight: 600;
          color: var(--blue);
          line-height: 1;
        }
        .epopee-stat-label {
          font-family: 'Montserrat', sans-serif;
          font-size: .52rem;
          font-weight: 500;
          letter-spacing: .12em;
          text-transform: uppercase;
          color: var(--ink2);
          margin-top: .35rem;
        }

        /* Right side */
        .epopee-right {
          padding: 2.2rem 2.2rem 2rem;
          display: flex;
          flex-direction: column;
          gap: 1.2rem;
        }
        .epopee-title {
          font-family: 'Cormorant Garamond', Georgia, serif;
          font-size: 2rem;
          font-weight: 600;
          color: var(--ink);
          margin: 0;
          line-height: 1.2;
        }
        .epopee-desc {
          font-family: 'Montserrat', sans-serif;
          font-size: .88rem;
          font-weight: 300;
          color: var(--ink2);
          line-height: 1.8;
          margin: 0;
        }
        .epopee-moment {
          display: flex;
          gap: .9rem;
          align-items: flex-start;
          padding: .9rem 1.1rem;
          background: var(--white);
          border: 1px solid var(--border);
        }
        .epopee-moment-bar {
          width: 3px;
          align-self: stretch;
          background: var(--gold);
          flex-shrink: 0;
        }
        .epopee-moment-label {
          font-family: 'Montserrat', sans-serif;
          font-size: .55rem;
          font-weight: 600;
          letter-spacing: .18em;
          text-transform: uppercase;
          color: var(--gold-dark);
          margin-bottom: .2rem;
        }
        .epopee-moment-text {
          font-family: 'Montserrat', sans-serif;
          font-size: .78rem;
          font-weight: 500;
          color: var(--ink);
        }
        .epopee-quote {
          margin: 0;
          padding: 0 0 0 1.2rem;
          border-left: 2px solid var(--gold);
          font-family: 'Cormorant Garamond', Georgia, serif;
          font-style: italic;
          font-size: 1.1rem;
          font-weight: 400;
          color: var(--ink);
          line-height: 1.6;
          position: relative;
        }
        .epopee-quote-mark {
          font-size: 2rem;
          color: var(--gold);
          line-height: 0;
          margin-right: .2rem;
          vertical-align: -.3em;
          opacity: .5;
        }
        .epopee-chart {
          background: var(--white);
          border: 1px solid var(--border);
          padding: .9rem 1.1rem;
        }
        .epopee-chart-head {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: .5rem;
          font-family: 'Montserrat', sans-serif;
          font-size: .6rem;
          letter-spacing: .12em;
          text-transform: uppercase;
          color: var(--ink2);
          font-weight: 600;
        }
        .epopee-chart-now {
          color: var(--blue);
          font-weight: 700;
        }

        /* Navigation */
        .epopee-nav {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 1rem;
          flex-wrap: wrap;
        }
        .epopee-nav-btn {
          display: inline-flex;
          align-items: center;
          gap: .5rem;
          padding: .7rem 1.1rem;
          background: var(--white);
          border: 1px solid var(--border);
          font-family: 'Montserrat', sans-serif;
          font-size: .68rem;
          font-weight: 600;
          letter-spacing: .12em;
          text-transform: uppercase;
          color: var(--ink);
          cursor: pointer;
          transition: all .2s ease;
        }
        .epopee-nav-btn:hover:not(:disabled) {
          background: var(--blue);
          color: var(--white);
          border-color: var(--blue);
        }
        .epopee-nav-btn:disabled {
          opacity: .3;
          cursor: not-allowed;
        }
        .epopee-nav-counter {
          font-family: 'Montserrat', sans-serif;
          font-size: .7rem;
          font-weight: 500;
          color: var(--ink2);
          letter-spacing: .1em;
        }

        /* Responsive */
        @media (max-width: 900px) {
          .epopee-chapter {
            grid-template-columns: 1fr;
          }
          .epopee-left {
            border-right: none;
            border-bottom: 1px solid var(--border);
            padding: 1.5rem;
          }
          .epopee-right {
            padding: 1.5rem;
          }
          .epopee-dropcap { font-size: 4.5rem; }
          .epopee-dropcap.is-future { font-size: 3rem; }
          .epopee-icon-wrap { padding: 1rem 0; }
          .epopee-icon { width: 60px; height: 60px; }
          .epopee-title { font-size: 1.5rem; }
        }
        @media (max-width: 768px) {
          .epopee-section { padding: 4rem 0; }
          .epopee-inner { padding: 0 1rem; }
          .epopee-timeline {
            padding: 1rem 0 2rem;
            gap: .5rem;
            overflow-x: auto;
            justify-content: flex-start;
            -webkit-overflow-scrolling: touch;
          }
          .epopee-dot-wrap { flex: 0 0 auto; min-width: 60px; }
          .epopee-line-bg, .epopee-line-fill { display: none; }
          .epopee-year { font-size: .7rem; }
          .epopee-year.is-active { font-size: .85rem; }
          .epopee-desc { font-size: .78rem; }
          .epopee-quote { font-size: 1rem; }
          .epopee-nav-btn { padding: .6rem .8rem; font-size: .6rem; }
          .epopee-nav-counter { font-size: .6rem; width: 100%; text-align: center; order: -1; }
        }
        @media (max-width: 500px) {
          .epopee-dropcap { font-size: 3.5rem; }
          .epopee-title { font-size: 1.3rem; }
          .epopee-stats { grid-template-columns: 1fr; }
        }
      `}</style>
    </section>
  );
};

export default InteractiveTimeline;
